import { useMemo } from 'react';
import { useDispatch } from 'react-redux';

import {
  REQUEST_TOGGLE_CONCEPT_PLAYLIST_GRAPHIC,
  REQUEST_UPDATE_PLAYLIST_GRAPHIC,
} from 'src/redux/reducers/playlistGraphic';
import {
  REQUEST_ANIMATE_IN_LIVE_GRAPHIC,
  REQUEST_ANIMATE_OUT_LIVE_GRAPHIC,
  REQUEST_UPDATE_LIVE_GRAPHIC,
} from 'src/redux/reducers/control';

import { HandleMediaOptions } from '../DisplayHubStory/types';

import HoverActions from './HoverActions';
import Placeholder from './Placeholder';
import Controls from './Controls';

type Props = {
  rowIndex: number;
  playlistGraphic: PlaylistGraphic;
  onHandleMedia: (properties: ImageProperties, options: HandleMediaOptions) => void;
};

function DisplayHubStoryGraphic({ rowIndex, playlistGraphic, onHandleMedia }: Props) {
  const dispatch = useDispatch();

  const imageProperty = useMemo<ImageProperties>(
    () => ({ id: `image${rowIndex + 1}`, name: '', svgPath: '', type: 'image' }),
    [rowIndex]
  );

  const { image: imageSrc } = playlistGraphic.propertiesValues;

  const liveIn = () =>
    dispatch({
      type: REQUEST_ANIMATE_IN_LIVE_GRAPHIC,
      payload: { playlistGraphicId: playlistGraphic.id },
    });

  const liveOut = () =>
    dispatch({
      type: REQUEST_ANIMATE_OUT_LIVE_GRAPHIC,
      payload: { playlistGraphicId: playlistGraphic.id },
    });

  const swap = () =>
    dispatch({
      type: REQUEST_UPDATE_LIVE_GRAPHIC,
      payload: { playlistGraphicId: playlistGraphic.id, payload: 'swap:fade' },
    });

  const handleToggleConceptPlaylistGraphic = () =>
    dispatch({
      type: REQUEST_TOGGLE_CONCEPT_PLAYLIST_GRAPHIC,
      payload: { playlistId: playlistGraphic.playlist.id, playlistGraphicId: playlistGraphic.id },
    });

  const handleUpdatePlaylistGraphic = (image: string | null) =>
    dispatch({
      type: REQUEST_UPDATE_PLAYLIST_GRAPHIC,
      payload: {
        graphic: {
          ...playlistGraphic,
          propertiesValues: { ...playlistGraphic.propertiesValues, image },
        },
      },
    });

  const handleAddMedia = () =>
    onHandleMedia(imageProperty, { mode: 'select', callback: handleUpdatePlaylistGraphic });

  const handleDeleteMedia = (e: React.MouseEvent<HTMLDivElement>) => {
    e.stopPropagation();
    onHandleMedia(imageProperty, { mode: 'remove', callback: handleUpdatePlaylistGraphic });
  };

  return (
    <div className="flex flex-col gap-1 cursor-default">
      <div className="relative flex items-center justify-center h-20 aspect-video w-full bg-gray-900 rounded-sm group">
        <HoverActions
          playlistGraphic={playlistGraphic}
          imageSrc={imageSrc}
          onAddMedia={handleAddMedia}
          onDeleteMedia={handleDeleteMedia}
          onToggleConcept={handleToggleConceptPlaylistGraphic}
        />
        {imageSrc ? (
          <img src={imageSrc} className="object-contain rounded-sm w-full h-full" />
        ) : (
          <Placeholder
            text={playlistGraphic.isConcept ? 'keep' : 'clear'}
            textColor={playlistGraphic.isConcept ? 'text-blue-500' : 'text-gray-500'}
          />
        )}
      </div>
      <Controls playlistGraphic={playlistGraphic} liveIn={liveIn} liveOut={liveOut} swap={swap} />
    </div>
  );
}

export default DisplayHubStoryGraphic;
